import type {
  CustomizationDemandDraft,
  CustomizationDemandDisplayGroup,
  CustomizationProjectSnapshot,
  CustomizationProjectUpdatePayload,
} from "../../customization";
import { getActualDemandRequiredMissingFields } from "../../customization/service/customizationService";
import { normalizeProvinceNames } from "../../../shared/region";

type FieldKind = "text" | "tag" | "number" | "summary";

type FieldSource = {
  key: string;
  label: string;
  kind?: FieldKind;
  value?: unknown;
};

type MobileDisplayField = CustomizationDemandDisplayGroup["fields"][number] & {
  missing?: boolean;
  required?: boolean;
};

export type CustomizationMobileRequiredStatus = {
  status: "pending" | "review" | "missing" | "complete";
  title: string;
  text: string;
};

/** 构造手机临时页需求展示分组。 */
export function buildCustomizationMobileDemandGroups(
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationDemandDisplayGroup[] {
  const summary = demand?.summary;
  const destinationDisplayText = formatMobileDestinationDisplay(demand);

  const customerRequirements = demand?.customerRequirements;
  return markMobileRequiredFields(
    markMobileRequiredMissingFields(
      markMobileChangedFields(
        compactMobileDemandGroups([
          group("base", "基础信息", [
            field("primarySchoolNameSnapshot", "高校", project?.primarySchoolNameSnapshot),
            field("customerName", "客户", project?.customerName),
            field("contactName", "联系人", project?.contactName),
            field("majorNames", "专业", demand?.relations?.majorNames, "tag"),
            field("departureStartDate", "出发日期", summary?.departureStartDate ?? demand?.departureStartDate),
            field("durationDays", "行程天数", summary?.durationDays ?? demand?.durationDays, "number"),
            field("studentCount", "出行人数", summary?.studentCount ?? demand?.studentCount, "number"),
            field("teacherCount", "老师人数", summary?.teacherCount ?? demand?.teacherCount, "number"),
            field("provinces", "目的地省", destinationDisplayText, "summary"),
            field("stayAreaNames", "目的地住宿", demand?.relations?.stayAreaNames, "tag"),
            field("pointNames", "考察点", demand?.relations?.pointNames, "tag"),
            field("dispatcherUserName", "第一计调", formatMobileDispatcherDisplay(project)),
          ]),
          group("customer", "客户需求", [
            field(
              "businessStatus",
              "业务状态",
              joinDisplayParts(
                customerRequirements?.customerType ?? project?.customerType,
                customerRequirements?.tripType ?? project?.tripType,
                customerRequirements?.signType ?? project?.signType,
                customerRequirements?.dealProgress ?? project?.dealProgress,
              ),
            ),
            field(
              "executionTeam",
              "执行团队",
              customerRequirements?.operatingCompany ?? project?.operatingCompany,
            ),
            field("teachingRequirements", "教学要求", demand?.customerRequirements?.teachingRequirements ?? demand?.teachingRequirements, "summary"),
            field("specialRequirements", "特殊需求", demand?.customerRequirements?.specialRequirements ?? demand?.specialRequirements, "summary"),
            field("schoolPickupRequirement", "接送要求", demand?.customerRequirements?.schoolPickupRequirement ?? demand?.schoolPickupRequirement, "summary"),
            field("invoiceRequirement", "发票要求", demand?.customerRequirements?.invoiceRequirement ?? demand?.invoiceRequirement, "summary"),
            field("remark", "备注", demand?.customerRequirements?.remark ?? demand?.remark, "summary"),
          ]),
          group("teacher", "老师需求", [
            field("teacherMealRequirement", "老师餐饮", demand?.teacherRequirements?.teacherMealRequirement ?? demand?.teacherMealRequirement, "summary"),
            field("teacherTicketRequirement", "老师门票", demand?.teacherRequirements?.teacherTicketRequirement ?? demand?.teacherTicketRequirement, "summary"),
            field("teacherTransportRequirement", "老师交通", demand?.teacherRequirements?.teacherTransportRequirement ?? demand?.teacherTransportRequirement, "summary"),
            field("teacherAccommodationRequirement", "老师住宿", demand?.teacherRequirements?.teacherAccommodationRequirement ?? demand?.teacherAccommodationRequirement, "summary"),
          ]),
          group("service", "服务标准", [
            field("guideCount", "领队/导游数", demand?.serviceStandard?.guideCount, "number"),
            field("guideNote", "领队/导游要求", demand?.serviceStandard?.guideNote, "summary"),
            field("includeTickets", "门票", formatBoolean(demand?.serviceStandard?.includeTickets, "包含考察点门票", "不包含考察点门票")),
            field("ticketNote", "门票要求", demand?.serviceStandard?.ticketNote, "summary"),
            field("includeMeals", "餐饮", formatBoolean(demand?.serviceStandard?.includeMeals, "全程含餐", "全程不含餐")),
            field("roundTripTransportNote", "行程大交通工具", demand?.serviceStandard?.roundTripTransportNote, "summary"),
            field("localTransportNote", "大巴车", demand?.serviceStandard?.localTransportNote, "summary"),
            field("accommodationNote", "团队住宿", demand?.serviceStandard?.accommodationNote, "summary"),
            field("mealStandards", "团队餐饮类型", demand?.serviceStandard?.mealStandards, "tag"),
            field("mealNote", "团队餐饮要求", demand?.serviceStandard?.mealNote, "summary"),
            field("companyVisitCount", "考察企业数", demand?.serviceStandard?.companyVisitCount, "number"),
            field("companyVisitNote", "考察企业要求", demand?.serviceStandard?.companyVisitNote, "summary"),
            field("intangibleHeritageCount", "考察非遗数", demand?.serviceStandard?.intangibleHeritageCount, "number"),
            field("intangibleHeritageNote", "考察非遗要求", demand?.serviceStandard?.intangibleHeritageNote, "summary"),
          ]),
        ]),
        demand,
      ),
      demand,
      project,
    ),
  );
}

/** 构造手机编辑初始值。 */
export function buildCustomizationMobileEditModel(
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationProjectUpdatePayload {
  const summary = demand?.summary;
  const customerRequirements = demand?.customerRequirements;
  const teacherRequirements = demand?.teacherRequirements;
  const serviceStandard = demand?.serviceStandard;
  return {
    contactId: project?.contactId,
    contactName: project?.contactName,
    customerId: project?.customerId,
    customerName: project?.customerName,
    dispatcherUserId: project?.dispatcherUserId,
    dispatcherUserName: project?.dispatcherUserName,
    budgetPerPerson: summary?.budgetPerPerson ?? demand?.budgetPerPerson,
    customerType: project?.customerType ?? customerRequirements?.customerType,
    dealProgress: project?.dealProgress ?? customerRequirements?.dealProgress,
    departureStartDate: summary?.departureStartDate ?? demand?.departureStartDate,
    destinationSummary: summary?.destinationSummary ?? demand?.destinationSummary,
    durationDays: summary?.durationDays ?? demand?.durationDays,
    accommodationNote: serviceStandard?.accommodationNote,
    companyVisitCount: serviceStandard?.companyVisitCount,
    companyVisitNote: serviceStandard?.companyVisitNote,
    guideCount: serviceStandard?.guideCount,
    guideNote: serviceStandard?.guideNote,
    includeMeals: serviceStandard?.includeMeals,
    includeTickets: serviceStandard?.includeTickets,
    invoiceRequirement: customerRequirements?.invoiceRequirement ?? demand?.invoiceRequirement,
    intangibleHeritageCount: serviceStandard?.intangibleHeritageCount,
    intangibleHeritageNote: serviceStandard?.intangibleHeritageNote,
    localTransportNote: serviceStandard?.localTransportNote,
    majorNames: normalizeEditNameList(demand?.relations?.majorNames),
    mealNote: serviceStandard?.mealNote,
    mealStandards: normalizeEditNameList(serviceStandard?.mealStandards),
    operatingCompany: project?.operatingCompany ?? customerRequirements?.operatingCompany,
    pointNames: normalizeEditNameList(demand?.relations?.pointNames),
    primarySchoolId: project?.primarySchoolId,
    primarySchoolName: project?.primarySchoolNameSnapshot,
    provinces: normalizeEditNameList(demand?.relations?.provinces),
    remark: customerRequirements?.remark ?? demand?.remark,
    roundTripTransportNote: serviceStandard?.roundTripTransportNote,
    schoolPickupRequirement:
      customerRequirements?.schoolPickupRequirement ?? demand?.schoolPickupRequirement,
    signType: project?.signType ?? customerRequirements?.signType,
    specialRequirements: customerRequirements?.specialRequirements ?? demand?.specialRequirements,
    stayAreaNames: normalizeEditNameList(demand?.relations?.stayAreaNames),
    studentCount: summary?.studentCount ?? demand?.studentCount,
    teacherAccommodationRequirement:
      teacherRequirements?.teacherAccommodationRequirement ?? demand?.teacherAccommodationRequirement,
    teacherCount: summary?.teacherCount ?? demand?.teacherCount,
    teacherMealRequirement:
      teacherRequirements?.teacherMealRequirement ?? demand?.teacherMealRequirement,
    teacherTicketRequirement:
      teacherRequirements?.teacherTicketRequirement ?? demand?.teacherTicketRequirement,
    teacherTransportRequirement:
      teacherRequirements?.teacherTransportRequirement ?? demand?.teacherTransportRequirement,
    teachingRequirements: customerRequirements?.teachingRequirements ?? demand?.teachingRequirements,
    ticketNote: serviceStandard?.ticketNote,
    totalCount: summary?.totalCount ?? demand?.totalCount,
    tripType: project?.tripType ?? customerRequirements?.tripType,
  };
}

/** 获取手机临时页必填项状态。 */
export function getCustomizationMobileRequiredStatus(
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationMobileRequiredStatus {
  if (!demand || !project) {
    return {
      status: "pending",
      text: "当前还没有结构化需求版本。",
      title: "待生成定制需求",
    };
  }
  const requiredSignals = resolveMobileRequiredSignals(demand, project);
  const missing = requiredSignals.missingLabels;
  const reviewReasons = requiredSignals.reviewReasons;
  if (missing.length > 0) {
    return {
      status: "missing",
      text: `缺少必填项：${missing.join("、")}`,
      title: "必填项状态",
    };
  }
  if (reviewReasons.length > 0) {
    return {
      status: "review",
      text: `必填项待确认：${reviewReasons.join("、")}`,
      title: "必填项待确认",
    };
  }
  return {
    status: "complete",
    text: "当前需求的关键字段已覆盖，可用于手机临时查看。",
    title: "必填项状态",
  };
}

/** 构造分组。 */
function group(key: string, title: string, fields: FieldSource[]): CustomizationDemandDisplayGroup {
  return {
    key,
    title,
    fields: fields.map((item) => ({
      key: item.key,
      kind: item.kind,
      label: item.label,
      value: formatValue(item.value),
      displayText: formatValue(item.value),
    })),
  };
}

/** 压缩手机端少量字段，减少单个字段在小屏上反复占行。 */
function compactMobileDemandGroups(
  groups: CustomizationDemandDisplayGroup[],
): CustomizationDemandDisplayGroup[] {
  return groups.map((item) => {
    if (item.key === "teacher") {
      return compactTeacherDemandGroup(item);
    }
    if (item.key === "service") {
      return compactServiceDemandGroup(item);
    }
    return item;
  });
}

/** 将需求版本 changedFields 应用到手机字段卡片。 */
function markMobileChangedFields(
  groups: CustomizationDemandDisplayGroup[],
  demand: CustomizationDemandDraft | null,
): CustomizationDemandDisplayGroup[] {
  const changedKeys = new Set(
    Object.keys(demand?.changedFields ?? {}).map(normalizeMobileChangedFieldKey),
  );
  if (changedKeys.size === 0) {
    return groups;
  }
  return groups.map((groupItem) => ({
    ...groupItem,
    fields: groupItem.fields.map((fieldItem) => ({
      ...fieldItem,
      changed: Boolean(fieldItem.changed || changedKeys.has(fieldItem.key)),
    })),
  }));
}

/** 将缺失必填项应用到手机字段卡片。 */
function markMobileRequiredMissingFields(
  groups: CustomizationDemandDisplayGroup[],
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationDemandDisplayGroup[] {
  const missingLabels = new Set(
    demand && project ? resolveMobileRequiredSignals(demand, project).highlightedLabels : [],
  );
  if (missingLabels.size === 0) {
    return groups;
  }
  return groups.map((groupItem) => ({
    ...groupItem,
    fields: groupItem.fields.map((fieldItem) => ({
      ...fieldItem,
      missing: isMobileFieldRequiredMissing(fieldItem as MobileDisplayField, missingLabels),
    })),
  }));
}

/** 仅校正后端已报告的旧缺项，不为没有缺项报告的历史版本凭空新增门禁。 */
function resolveMobileRequiredSignals(
  demand: CustomizationDemandDraft,
  project: CustomizationProjectSnapshot,
): {
  highlightedLabels: string[];
  missingLabels: string[];
  reviewReasons: string[];
} {
  const actualMissingLabels = new Set(getActualDemandRequiredMissingFields(demand, project));
  const highlightedLabels: string[] = [];
  const missingLabels: string[] = [];
  const reviewReasons: string[] = [];
  for (const reason of demand.needsHumanReviewReasons ?? []) {
    const isMissingReason = reason.includes("缺少") || reason.includes("待补");
    const labels = extractMobileMissingRequiredLabels(reason);
    if (!isMissingReason) {
      reviewReasons.push(reason);
      highlightedLabels.push(...labels);
      continue;
    }
    for (const label of labels) {
      if (isReportedMobileMissingStillEmpty(label, demand, actualMissingLabels)) {
        missingLabels.push(label);
        highlightedLabels.push(label);
      }
    }
  }
  return {
    highlightedLabels: dedupeMobileLabels(highlightedLabels),
    missingLabels: dedupeMobileLabels(missingLabels),
    reviewReasons: dedupeMobileLabels(reviewReasons),
  };
}

/** 从缺项原因中提取业务字段名称。 */
function extractMobileMissingRequiredLabels(reason: string): string[] {
  return reason
    .replace(/^(缺少|待补|请补充|请填写|必填项待确认)/, "")
    .split(/[、,，；;：:\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** 判断已报告的缺项在当前真实字段值中是否仍为空。 */
function isReportedMobileMissingStillEmpty(
  label: string,
  demand: CustomizationDemandDraft,
  actualMissingLabels: Set<string>,
): boolean {
  if (["目的地省", "目的地省份", "目的省份"].includes(label)) {
    return !hasMobileRequiredValue(demand.relations?.provinces);
  }
  if (["目的地住宿", "目的地住宿点", "住宿点"].includes(label)) {
    return !hasMobileRequiredValue(demand.relations?.stayAreaNames);
  }
  if (label === "考察点") {
    return !hasMobileRequiredValue(demand.relations?.pointNames);
  }
  return isMobileMissingLabelHit(label, actualMissingLabels);
}

/** 判断手机必填字段值是否已填写。 */
function hasMobileRequiredValue(value: unknown): boolean {
  if (value == null || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/** 按首次出现顺序去重手机字段标签。 */
function dedupeMobileLabels(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

/** 将 PC 必填字段星号应用到手机字段卡片。 */
function markMobileRequiredFields(
  groups: CustomizationDemandDisplayGroup[],
): CustomizationDemandDisplayGroup[] {
  return groups.map((groupItem) => ({
    ...groupItem,
    fields: groupItem.fields.map((fieldItem) => ({
      ...fieldItem,
      required: isMobileDemandFieldRequired(fieldItem as MobileDisplayField),
    })),
  }));
}

/** 判断手机字段是否属于 PC 同口径必填项。 */
function isMobileDemandFieldRequired(fieldItem: MobileDisplayField): boolean {
  const aliases = mobileRequiredLabelAliases[fieldItem.label] ?? [fieldItem.label];
  return mobileRequiredFieldKeys.has(fieldItem.key) || aliases.some((label) => mobileRequiredFieldLabels.has(label));
}

/** 判断手机字段是否命中缺失必填项。 */
function isMobileFieldRequiredMissing(
  fieldItem: MobileDisplayField,
  missingLabels: Set<string>,
): boolean {
  const aliases = mobileRequiredLabelAliases[fieldItem.label] ?? [fieldItem.label];
  return aliases.some((label) => isMobileMissingLabelHit(label, missingLabels));
}

/** 判断缺项原因是否明确命中当前业务标签。 */
function isMobileMissingLabelHit(label: string, missingLabels: Set<string>): boolean {
  if (missingLabels.has(label)) {
    return true;
  }
  if (label.length < 3) {
    return false;
  }
  for (const reason of missingLabels) {
    if (reason.includes(label) || label.includes(reason)) {
      return true;
    }
  }
  return false;
}

const mobileRequiredLabelAliases: Record<string, string[]> = {
  高校: ["高校", "出发高校", "出发高校/组织"],
  客户: ["客户"],
  联系人: ["联系人", "客户联系人"],
  目的地省: ["目的地省", "目的地省份", "目的省份"],
  目的地住宿: ["目的地住宿", "目的地住宿点", "住宿点"],
  考察点: ["考察点"],
  第一计调: ["第一计调", "计调人员"],
  业务状态: ["业务状态", "客户类型", "出行类型", "签单类型", "成单进度"],
  执行团队: ["执行团队", "操作公司"],
  老师安排: ["老师安排", "老师餐饮", "老师门票", "老师交通", "老师住宿"],
  领队导游: ["领队导游", "领队/导游", "领队/导游数"],
  交通住宿: ["交通住宿", "大交通", "行程大交通工具"],
};

const mobileRequiredFieldKeys = new Set([
  "primarySchoolNameSnapshot",
  "customerName",
  "contactName",
  "businessStatus",
  "executionTeam",
  "dispatcherUserName",
  "provinces",
  "departureStartDate",
  "durationDays",
  "studentCount",
  "teacherCount",
  "schoolPickupRequirement",
  "invoiceRequirement",
  "teacherArrangement",
  "majorNames",
  "pointNames",
  "stayAreaNames",
  "trafficAndAccommodation",
  "guideArrangement",
]);

const mobileRequiredFieldLabels = new Set(
  Object.values(mobileRequiredLabelAliases).flatMap((aliases) => aliases),
);

/** 规整后端和本地草稿的 changedFields key。 */
function normalizeMobileChangedFieldKey(key: string): string {
  const normalized = toCamelCase(key.trim().replace(/\./g, "_"));
  const aliases: Record<string, string> = {
    accommodationNote: "trafficAndAccommodation",
    companyVisitCount: "visitArrangement",
    companyVisitNote: "visitArrangement",
    contactId: "contactName",
    customerId: "customerName",
    destinationSummary: "provinces",
    guideCount: "guideArrangement",
    guideNote: "guideArrangement",
    intangibleHeritageCount: "visitArrangement",
    intangibleHeritageNote: "visitArrangement",
    localTransportNote: "trafficAndAccommodation",
    mealNote: "ticketAndMeal",
    primarySchoolId: "primarySchoolNameSnapshot",
    primarySchoolName: "primarySchoolNameSnapshot",
    provinceNames: "provinces",
    relationProvinces: "provinces",
    relationsProvinces: "provinces",
    relationMajorNames: "majorNames",
    relationsMajorNames: "majorNames",
    relationPointNames: "pointNames",
    relationsPointNames: "pointNames",
    relationStayAreaNames: "stayAreaNames",
    relationsStayAreaNames: "stayAreaNames",
    roundTripTransportNote: "trafficAndAccommodation",
    teacherAccommodationRequirement: "teacherArrangement",
    teacherMealRequirement: "teacherArrangement",
    teacherTicketRequirement: "teacherArrangement",
    teacherTransportRequirement: "teacherArrangement",
    ticketNote: "ticketAndMeal",
  };
  if (aliases[normalized]) {
    return aliases[normalized];
  }
  if (normalized.startsWith("serviceStandard")) {
    return uncapitalize(normalized.slice("serviceStandard".length));
  }
  if (normalized.startsWith("demand")) {
    return uncapitalize(normalized.slice("demand".length));
  }
  return normalized;
}

/** snake_case 转 camelCase。 */
function toCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/** 首字母小写。 */
function uncapitalize(value: string): string {
  return value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;
}

/** 合并老师需求。 */
function compactTeacherDemandGroup(
  groupItem: CustomizationDemandDisplayGroup,
): CustomizationDemandDisplayGroup {
  const values = readGroupFieldValues(groupItem);
  return {
    ...groupItem,
    fields: [
      compactField(
        "teacherArrangement",
        "老师安排",
        joinDisplayParts(
          labelValue("老师餐饮", values.teacherMealRequirement),
          labelValue("老师门票", values.teacherTicketRequirement),
          labelValue("老师交通", values.teacherTransportRequirement),
          labelValue("老师住宿", values.teacherAccommodationRequirement),
        ),
      ),
    ],
  };
}

/** 合并服务标准。 */
function compactServiceDemandGroup(
  groupItem: CustomizationDemandDisplayGroup,
): CustomizationDemandDisplayGroup {
  const values = readGroupFieldValues(groupItem);
  return {
    ...groupItem,
    fields: [
      compactField(
        "guideArrangement",
        "领队/导游",
        joinDisplayParts(
          labelValue("人数", formatCount(values.guideCount, "人")),
          labelValue("要求", values.guideNote),
        ),
      ),
      compactField(
        "ticketAndMeal",
        "门票餐饮",
        joinDisplayParts(
          labelValue("门票要求", values.ticketNote),
          labelValue("餐饮要求", values.mealNote),
        ),
      ),
      compactField(
        "trafficAndAccommodation",
        "交通住宿",
        joinDisplayParts(
          labelValue("行程大交通工具", values.roundTripTransportNote),
          labelValue("大巴", values.localTransportNote),
          labelValue("住宿", values.accommodationNote),
        ),
      ),
      compactField(
        "visitArrangement",
        "企业非遗",
        joinDisplayParts(
          labelValue("企业数", formatCount(values.companyVisitCount, "个")),
          labelValue("企业要求", values.companyVisitNote),
          labelValue("非遗数", formatCount(values.intangibleHeritageCount, "个")),
          labelValue("非遗要求", values.intangibleHeritageNote),
        ),
      ),
    ],
  };
}

/** 构造压缩字段。 */
function compactField(
  key: string,
  label: string,
  value: string,
): CustomizationDemandDisplayGroup["fields"][number] {
  const displayText = value || "--";
  return {
    key,
    kind: "summary",
    label,
    value: displayText,
    displayText,
  };
}

/** 读取分组字段值。 */
function readGroupFieldValues(groupItem: CustomizationDemandDisplayGroup): Record<string, string> {
  return Object.fromEntries(
    groupItem.fields.map((item) => [
      normalizeDemandFieldKey(item.key),
      normalizeMeaningfulDisplayText(item.displayText || item.value),
    ]),
  );
}

/** 格式化带标签片段。 */
function labelValue(label: string, value?: unknown): string {
  const text = normalizeMeaningfulDisplayText(value);
  return text ? `${label}：${text}` : "";
}

/** 格式化数量。 */
function formatCount(value: unknown, unit: string): string {
  const text = normalizeMeaningfulDisplayText(value);
  return text ? `${text}${unit}` : "";
}

/** 规整字段 key。 */
function normalizeDemandFieldKey(key: string): string {
  return key.trim().replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/** 手机端目的地展示优先使用省份，和 PC 需求定制字段保持一致。 */
function formatMobileDestinationDisplay(demand: CustomizationDemandDraft | null): string | undefined {
  const provinces = normalizeProvinceNames(demand?.relations?.provinces ?? []);
  if (provinces?.length) {
    return provinces.join("、");
  }
  return demand?.summary?.destinationSummary ?? demand?.destinationSummary;
}

/** 清理展示文本，过滤占位值。 */
function normalizeMeaningfulDisplayText(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeMeaningfulDisplayText(item)).filter(Boolean).join(" / ");
  }
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value).trim();
  return text && text !== "--" ? text : "";
}

/** 构造字段。 */
function field(key: string, label: string, value?: unknown, kind?: FieldKind): FieldSource {
  return { key, kind, label, value };
}

/** 格式化布尔值。 */
function formatBoolean(value: boolean | undefined, truthy: string, falsy: string): string {
  if (value === undefined) {
    return "";
  }
  return value ? truthy : falsy;
}

/** 拼接少量字段，空值自动剔除。 */
function joinDisplayParts(...values: Array<unknown>): string {
  const parts = values.flatMap((value) => normalizeDisplayParts(value));
  return parts.length > 0 ? parts.join(" / ") : "";
}

/** 规整展示片段。 */
function normalizeDisplayParts(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized ? [normalized] : [];
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? [String(value)] : [];
  }
  if (typeof value === "boolean") {
    return [value ? "是" : "否"];
  }
  return [];
}

/** 规整编辑用名称数组。 */
function normalizeEditNameList(value?: string[]): string[] | undefined {
  const items = value?.map((item) => item.trim()).filter(Boolean) ?? [];
  return items.length > 0 ? items : undefined;
}

/** 展示第一计调姓名与电话，不影响编辑提交字段。 */
function formatMobileDispatcherDisplay(project: CustomizationProjectSnapshot | null): string | undefined {
  const name = normalizeMeaningfulDisplayText(project?.dispatcherUserName);
  const phone = normalizeMeaningfulDisplayText(project?.dispatcherUserPhone);
  return [name, phone].filter(Boolean).join(" / ") || undefined;
}

/** 格式化展示值。 */
function formatValue(value: unknown): string {
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item).trim()).filter(Boolean);
    return items.length > 0 ? items.join(" / ") : "--";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "--";
  }
  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }
  if (typeof value === "string") {
    return value.trim() || "--";
  }
  return "--";
}
